import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";
import { uploadFileRequest } from "../../utils/uploadRequest";

const getToken = () => isAuthenticated()?.token || "";

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });
  return query.toString();
};

const apiFetch = async (path, { method = "GET", body, token } = {}) => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const resolvedToken = token || getToken();
  if (resolvedToken) headers.Authorization = `Bearer ${resolvedToken}`;

  const response = await fetch(`${elakay_API}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || `Erreur investment ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return data;
};

// `purpose: "application"` stores the file in the protected zone (CVs and
// other job-application files, readable through signed links only).
const uploadInvestmentFile = async (file, token, { purpose } = {}) => {
  const headers = {
    Accept: "application/json",
  };
  const resolvedToken = token || getToken();
  if (resolvedToken) headers.Authorization = `Bearer ${resolvedToken}`;

  return uploadFileRequest({
    url: `${elakay_API}/documents/upload${purpose ? `?purpose=${encodeURIComponent(purpose)}` : ""}`,
    feature: "document",
    file,
    headers,
    throwOnError: true,
  });
};

export const uploadInvestmentDocument = uploadInvestmentFile;

export const uploadInvestmentImage = uploadInvestmentFile;

export const getInvestmentProjects = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/investments/public${query ? `?${query}` : ""}`, { token });
};

export const getInvestmentProject = (slugOrId, token) =>
  apiFetch(`/investments/public/${encodeURIComponent(slugOrId)}`, { token });

export const getMyInvestments = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/investments/user/mine${query ? `?${query}` : ""}`, { token });
};

export const getMyInvestmentCredits = (token) =>
  apiFetch("/investments/user/credits", { token });

export const cashOutInvestmentCredit = (paymentId, payload = {}, token) =>
  apiFetch(`/investments/user/credits/${paymentId}/cash-out`, {
    method: "POST",
    body: payload,
    token,
  });

// Server-authoritative preview shown on step 1 of the two-step Secure
// Payment flow -- never persists anything, just returns the official
// min/max/remaining/percentage/role/quota numbers for the amount being
// considered so the frontend never has to compute or trust its own math.
export const previewInvestment = (projectId, payload, token) =>
  apiFetch(`/investments/${projectId}/preview`, {
    method: "POST",
    body: payload,
    token,
  });

export const createInvestmentPaymentIntent = (projectId, payload, token) =>
  apiFetch(`/investments/${projectId}/payment-intent`, {
    method: "POST",
    body: payload,
    token,
  });

export const confirmInvestmentPayment = (payload, token) =>
  apiFetch("/investments/user/payments/confirm", {
    method: "POST",
    body: payload,
    token,
  });

export const createInvestmentProject = (payload, token) =>
  apiFetch("/investments/admin/projects", {
    method: "POST",
    body: payload,
    token,
  });

export const getAdminInvestmentProjects = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/investments/admin/projects${query ? `?${query}` : ""}`, { token });
};

export const updateInvestmentProject = (id, payload, token) =>
  apiFetch(`/investments/admin/projects/${id}`, {
    method: "PATCH",
    body: payload,
    token,
  });

export const changeInvestmentProjectStatus = (id, payload, token) =>
  apiFetch(`/investments/admin/projects/${id}/status`, {
    method: "PATCH",
    body: payload,
    token,
  });

export const notifyInvestmentProjectAccess = (id, token) =>
  apiFetch(`/investments/admin/projects/${id}/access/notify`, {
    method: "POST",
    body: {},
    token,
  });

// params accepts q, role, status, sortBy, order, skip, limit -- all applied
// server-side (see getProjectInvestmentRows in elakay_controllers/investment.js).
export const getInvestmentProjectInvestors = (id, params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/investments/admin/projects/${id}/investors${query ? `?${query}` : ""}`, {
    token,
  });
};

export const getInvestmentProjectInvestorDetail = (projectId, investorId, token) =>
  apiFetch(`/investments/admin/projects/${projectId}/investors/${investorId}`, { token });

// Recherche en lecture seule (ne crée jamais d'utilisateur) pour le modal
// "Ajouter un investisseur (gratuit)" de l'Investment Dashboard.
export const searchInvestorCandidates = (email, token) =>
  apiFetch(`/investments/admin/users/search?email=${encodeURIComponent(email)}`, { token });

// Step-up auth : confirme le mot de passe du compte Family déjà connecté
// avant d'autoriser l'ajout gratuit d'un investisseur.
export const verifyMyPassword = (password, token) =>
  apiFetch("/user/verify-password", { method: "POST", body: { password }, token });

export const createManualInvestment = (projectId, payload, token) =>
  apiFetch(`/investments/admin/projects/${projectId}/manual-investment`, {
    method: "POST",
    body: payload,
    token,
  });

export const createInvestmentDistribution = (projectId, payload, token) =>
  apiFetch(`/investments/admin/projects/${projectId}/distributions`, {
    method: "POST",
    body: payload,
    token,
  });
